/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Box } from '@mui/material';
import { type ReactNode, useState, useContext, useEffect } from 'react';

import type { SidebarComponentProps } from '../../constants/tabs';
import { SearchContext } from '../../contexts/SearchContext';
import { type PresetView, getPresets } from '../../utils/getPresets/getPresets';
import { isTextMatchedBySearchQuery } from '../../utils/isTextMatchedBySearchQuery/isTextMatchedBySearchQuery';
import { NoResultsWrapper } from '../NoResultsWrapper/NoResultsWrapper';
import { SearchField } from '../SearchField/SearchField';
import { SidebarSectionHeader } from '../SidebarSectionHeader/SidebarSectionHeader';
import { PresetDetails } from './PresetDetails/PresetDetails';
import { GTMContext } from '../../contexts/GTMContext';
import { GTM_EVENT_SEARCH } from '../../constants/GTMEventNames';

import {
	ImageEditorBalloonActive,
	ImageEditorBalloonWideActive,
	ImageEditorClassicActive,
	ImageEditorClassicWideActive,
	ImageEditorDecoupledActive,
	ImageEditorDecoupledWideActive,
	ImageEditorInlineActive,
	ImageEditorInlineWideActive,
	ImagePresetArticleActive,
	ImagePresetArticleWideActive,
	ImagePresetDocumentActive,
	ImagePresetDocumentWideActive,
	ImagePresetEmailActive,
	ImagePresetEmailWideActive,
	ImagePresetHtmlActive,
	ImagePresetHtmlWideActive,
	ImagePresetMicroActive,
	ImagePresetMicroWideActive,
	ImagePresetNotionActive,
	ImagePresetNotionWideActive
} from '../../assets';
import { EditorSettingsContext } from '../../contexts/EditorSettingsContext';

const presetsContainerStyle = {
	paddingRight: '8px',
	display: 'grid',
	gap: '8px',
	gridTemplateColumns: 'var(--sidebar-presets-grid-template)'
};

export function PresetsSelection( {
	isSelectedPresetModified,
	activeTab,
	onTabChange
}: SidebarComponentProps ): ReactNode {
	const { preset: selectedPreset, onPresetChange } = useContext( EditorSettingsContext );
	const { triggerGTMEvent } = useContext( GTMContext );

	const presets = getPresets();
	const [ searchQuery, setSearchQuery ] = useState( '' );

	const filteredPresets = getFilteredPresets( searchQuery, presets );
	const hasAnyResults = !!filteredPresets.length;

	useEffect( () => {
		preloadDynamicImages();
	}, [] );

	useEffect( () => {
		if ( searchQuery.length < 3 ) {
			return;
		}

		triggerGTMEvent( {
			event: GTM_EVENT_SEARCH,
			stage: 'presets',
			view: undefined,
			query: searchQuery,
			results: filteredPresets.length
		} );
	}, [ filteredPresets.length, searchQuery, triggerGTMEvent ] );

	return (
		<SearchContext.Provider value={ searchQuery }>
			<Box gap={ '16px' } display={ 'flex' } flexDirection={ 'column' }>
				<SidebarSectionHeader
					title={ activeTab.tabName }
					description={ activeTab.description }
				/>
				<SearchField onSearch={ setSearchQuery } placeholder='e.g. document' label='Search for presets'/>
			</Box>
			<Box sx={ { marginTop: '8px', overflowY: 'auto', scrollbarWidth: 'thin' } }>
				<NoResultsWrapper shouldShowResults={ hasAnyResults }>
					<Box sx={ presetsContainerStyle } data-testid='presets-container' >
						{ filteredPresets.map( preset => (
							<PresetDetails
								key={ preset.name }
								name={ preset.name }
								description={ preset.description }
								isActive={ isPresetActive( selectedPreset, preset ) }
								isModified={ isSelectedPresetModified }
								onSelect={ () => onPresetChange( preset ) }
								onContinue={ () => onTabChange( activeTab.index + 1 ) }
							/>
						) ) }
					</Box>
				</NoResultsWrapper>
			</Box>
		</SearchContext.Provider>
	);
}

function isPresetActive( selectedPreset: PresetView | null, renderedPreset: { name: string } ) {
	return selectedPreset?.name === renderedPreset.name;
}

function getFilteredPresets( searchQuery: string, presets: Array<PresetView> ) {
	return presets.filter( preset =>
		[ preset.name, preset.description ].some( text => isTextMatchedBySearchQuery( text, searchQuery ) )
	);
}

function preloadDynamicImages() {
	const images = [
		ImageEditorBalloonActive,
		ImageEditorBalloonWideActive,
		ImageEditorClassicActive,
		ImageEditorClassicWideActive,
		ImageEditorDecoupledActive,
		ImageEditorDecoupledWideActive,
		ImageEditorInlineActive,
		ImageEditorInlineWideActive,
		ImagePresetArticleActive,
		ImagePresetArticleWideActive,
		ImagePresetDocumentActive,
		ImagePresetDocumentWideActive,
		ImagePresetEmailActive,
		ImagePresetEmailWideActive,
		ImagePresetHtmlActive,
		ImagePresetHtmlWideActive,
		ImagePresetMicroActive,
		ImagePresetMicroWideActive,
		ImagePresetNotionActive,
		ImagePresetNotionWideActive
	];

	for ( const image of images ) {
		new Image().src = image;
	}
}
